/**
 * Gym-vs-Gym leaderboard — aggregates strength scores by organization (gym).
 *
 * Returns the top 10 gyms ranked by average strength score, with member count.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export interface GymBattleRow {
  gym_id: string;
  gym_name: string;
  avg_strength: number;
  member_count: number;
}

/**
 * Fetch gym battle leaderboard.
 *
 * Strategy: pull all ranked profiles that belong to an organization, then
 * aggregate in-memory (Supabase JS client doesn't support GROUP BY natively).
 */
export async function fetchGymBattleLeaderboard(
  client: SupabaseClient,
): Promise<{ rows: GymBattleRow[]; error: string | null }> {
  // Fetch active gym memberships to exclude inactive members from battle
  const { data: activeMembers } = await client
    .from('gym_memberships')
    .select('user_id, gym_id')
    .neq('is_active', false); // null = active (default), true = active, false = inactive
  const activeMemberSet = new Set(
    (activeMembers ?? []).map((m: any) => `${m.user_id}:${m.gym_id}`)
  );

  // Fetch profiles that have both a gym and a strength score
  const { data, error } = await client
    .from('profiles')
    .select('id, gym_id, home_gym_name, duel_standard_strength_kg')
    .not('gym_id', 'is', null)
    .not('duel_standard_strength_kg', 'is', null)
    .gt('duel_standard_strength_kg', 0);

  if (error) return { rows: [], error: error.message };
  if (!data || data.length === 0) return { rows: [], error: null };

  // Fetch home gym IDs to exclude from battle
  const gymIds = [...new Set(data.map((r: any) => r.gym_id as string))];
  const { data: homeGyms } = await client
    .from('gyms')
    .select('id')
    .in('id', gymIds)
    .eq('is_home_gym', true);
  const homeGymIds = new Set((homeGyms || []).map((g: any) => g.id));

  // Collect all member scores per gym, excluding home gyms
  const gymMap = new Map<string, { gym_name: string; scores: number[] }>();

  for (const row of data) {
    const gid = row.gym_id as string;
    if (homeGymIds.has(gid)) continue; // Skip home gyms
    // Skip inactive members (respects member management toggle)
    if (activeMemberSet.size > 0 && !activeMemberSet.has(`${row.id}:${gid}`)) continue;
    const existing = gymMap.get(gid);
    if (existing) {
      existing.scores.push(row.duel_standard_strength_kg as number);
    } else {
      gymMap.set(gid, {
        gym_name: (row.home_gym_name as string) || 'Unknown Gym',
        scores: [row.duel_standard_strength_kg as number],
      });
    }
  }

  // Score each gym by average of top 10 members (incentivizes inclusivity)
  const rows: GymBattleRow[] = [];
  for (const [gym_id, agg] of gymMap) {
    const sorted = agg.scores.sort((a, b) => b - a);
    const topN = sorted.slice(0, 10);
    const avgScore = topN.reduce((sum, s) => sum + s, 0) / topN.length;
    rows.push({
      gym_id,
      gym_name: agg.gym_name,
      avg_strength: Math.round(avgScore),
      member_count: agg.scores.length, // total gym size, not just top 10
    });
  }

  rows.sort((a, b) => b.avg_strength - a.avg_strength);

  return { rows: rows.slice(0, 10), error: null };
}
